import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { createRoot } from 'react-dom/client';
import { connect, Provider } from 'react-redux';
import { CookiesProvider } from 'react-cookie';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';

import Api from './api/Api';
import { setHeFetchConfig } from './api/HeFetch';
import Mobile from './utils/Mobile';
import { replaceElement, insertElementBefore, appendElementBefore } from './utils/react';
import Storage from './utils/Storage';

import i18n from './i18n';

import store from './store';

import { fetchTopMenuNotifications } from './actions/notifications';

import { CurrencyProvider } from './components/currency';
import GeoIPProvider from './components/geoip/GeoIPProvider';
import AnalyticsProvider from './components/analytics/AnalyticsProvider';
import useAnalytics from './components/analytics/useAnalytics';
import AuthProvider from './components/auth/AuthProvider';
import UserContext from './components/auth/UserContext';
import EmailVerification from './components/user/verification/EmailVerification';
import PhoneBanner from './components/user/verification/PhoneBanner';
import Headband from './components/headband/Headband';
import Footer from './components/footer/footer';

import CurrencySwitcher from './components/layout/CurrencySwitcher';
import LanguageSwitcher from './components/layout/LanguageSwitcher';
import MenuProfile from './components/layout/header/MenuProfile';
import MenuGuestpoints from './components/layout/header/MenuGuestpoints';
import Signin from './components/popup/Signin';
import Signup from './components/popup/Signup';
import NavSearchBar from './components/search/NavSearchBar';
import Stars from './components/rating/Stars';
import TermsOfUsePopup, { termsOfUseTourName } from './components/popup/TermsOfUsePopup';
import UploadListWithCaption from './components/upload/UploadListWithCaption';
import UploadProfile from './components/profile/Upload';
import Analytics from './utils/analytics';
import DiscoverCollection from './components/footer/discover';
import Iterable from './utils/iterable';
import LHSHelpMenuItem from './components/layout/header/LHSHelpMenuItem';
import { Icon } from '@homeexchange/design';

// Modernizr
import '../modernizr';
import '@homeexchange/design/design.css';
import '@homeexchange/design/global.css';
import '@homeexchange/landing-page/landing-page.css';
import '@homeexchange/navigation-bar/navigation-bar.css';
import '@homeexchange/dashboard/dashboard.css';
import '@homeexchange/footer/footer.css';
import '@homeexchange/home/home.css';
import '@homeexchange/conversations/conversations.css';
import '@homeexchange/search/search.css';
import '@homeexchange/payment/payment.css';
import {
    setNavigationBarFetchConfig,
    CommunicationBanner,
    BannerProvider
} from '@homeexchange/navigation-bar';
import { createPortal } from 'react-dom';
import NavigationBarOffline from './components/header/NavigationBarOffline';
import BannerBodyClassSync from './components/header/BannerBodyClassSync';

// Add CSS class to tell if device handles hover or not
jQuery(document).on('mq4hsChange', (e) => {
    if (e.trueHover) {
        jQuery(document.documentElement).toggleClass('support-hover', true);
    } else {
        jQuery(document.documentElement).toggleClass('no-support-hover', true);
    }
});
require('mq4-hover-shim');

// jQuery
import $ from 'jquery';

// Hopscotch
import hopscotch from 'hopscotch';
window.hopscotch = hopscotch;
import 'hopscotch/dist/css/hopscotch.min.css';

// Tooltipster
import 'tooltipster';
import 'tooltipster/dist/css/tooltipster.bundle.min.css';
import 'tooltipster/dist/css/plugins/tooltipster/sideTip/themes/tooltipster-sideTip-light.min.css';

// SweetAlert
import sweetAlert from './utils/sweetAlert';
window.sweetAlert = sweetAlert;

// import lib's css
import 'kalendae/build/kalendae.css';
import 'nouislider/distribute/nouislider.min.css';

// Moment
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import Tour from './utils/Tour';
import useGTM from './components/analytics/useGTM';
import TrackingTwig from './tracking-twig';

import { fetchingStatuses } from './reducers/loading';
import { ReactivityLevel } from './components/user/ReactivityLevel';
import { getDynamicUrl } from './middleware/lokaliseUrlMiddleware';
const moment = extendMoment(Moment);
moment.locale(i18n.language);

setHeFetchConfig();

// Facebook SDK
window.fbAsyncInit = function () {
    const appId = $('meta[property="fb:app_id"]').attr('content');
    window.FB.init({
        appId,
        cookie: true, // enable cookies to allow the server to access the session
        xfbml: true, // parse social plugins on this page
        version: 'v3.2' // use graph api version 3.2
    });
};

$(document).ready(() => {
    Mobile.changeLinks();
});

window.gtg = Object.assign({}, window.gtg);

// expose API
window.Api = Api;

if (window.gtg.shouldClearLocalStorage) {
    Storage.clearLocalStorage();
}

const isconnected = document.getElementsByClassName('layout-login').length > 0;

const navigationBarOffline = document.getElementById('navigation-bar-offline');
const isHomepage = document.getElementById('home-page') !== null;
if (navigationBarOffline && !isconnected) {
    const isHomeView = document.getElementById('home-view') !== null;
    const isCmsPage = document.getElementById('cms-page') !== null;
    const isScrollable = (isHomeView || isHomepage) && !isCmsPage;
    const urlParams = new URLSearchParams(window.location.search);
    const hasSponsorKey = urlParams.has('sponsorkey');
    const hasUserGroupKey = urlParams.has('usergroupkey');
    const communicationBannerEl = document.getElementById('communication-banner');
    setNavigationBarFetchConfig(getDynamicUrl(process.env.URL_BFF));
    createRoot(navigationBarOffline).render(
        <Provider store={store}>
            <CurrencyProvider>
                <GeoIPProvider>
                    <BannerProvider>
                        <BannerBodyClassSync />
                        {communicationBannerEl &&
                            isHomepage &&
                            createPortal(
                                <CommunicationBanner
                                    locale={i18n.language}
                                    area={isHomepage && 'homepage'}
                                />,
                                communicationBannerEl
                            )}
                        <NavigationBarOffline
                            isScrollable={isScrollable}
                            searchBarAlwaysVisible={isHomeView || hasSponsorKey || hasUserGroupKey}
                            appearance={!isCmsPage ? 'dark' : 'light'}
                        />
                    </BannerProvider>
                </GeoIPProvider>
            </CurrencyProvider>
        </Provider>
    );

    const eventBridge = {
        'navigation-bar:open-signin': 'open_signin_popup',
        'navigation-bar:open-signup': 'open_signup_popup',
        'legacy:open-signin': 'open_signin_popup',
        'legacy:open-signup': 'open_signup_popup'
    };
    Object.entries(eventBridge).forEach(([source, target]) => {
        window.addEventListener(source, () => document.dispatchEvent(new CustomEvent(target)));
    });
}

const iterable = Iterable.getIterableInstance();

function InjectADAScript({ auth }) {
    React.useEffect(() => {
        if (window.adaSettings === null) {
            return;
        }
        if (auth?.accessToken) {
            if (window.adaSettings) {
                window.adaSettings = {
                    ...window.adaSettings,
                    sensitiveMetaFields: { access_token: auth.accessToken }
                };
            } else {
                window.adaSettings = {
                    sensitiveMetaFields: { access_token: auth.accessToken }
                };
            }
        }
    }, [auth]);

    return null;
}

InjectADAScript.propTypes = {
    auth: PropTypes.shape({
        accessToken: PropTypes.string
    })
};

const ConnectedInjectADAScript = connect((state) => ({
    auth: state.auth,
    user: state.user
}))(InjectADAScript);

createRoot(document.createElement('div')).render(
    <Provider store={store}>
        <Router>
            <ConnectedInjectADAScript />
        </Router>
    </Provider>
);

// Track page viewed
function PageViewTrack({ user, paymentMethods, loading, auth }) {
    const { fetchDataLayer } = useGTM(user, paymentMethods);
    const { trackGTM } = useAnalytics();
    const [userEmail, setUserEmail] = React.useState(null);

    React.useEffect(() => {
        const allLoading =
            loading.userFetchingStatus === fetchingStatuses.FINISHED &&
            loading.loyaltyFetchingStatus === fetchingStatuses.FINISHED &&
            loading.paymentMethodsFetchingStatus === fetchingStatuses.FINISHED;

        // if user is unlogged OR he's logged and his informations are loaded we want to track
        if (!auth.accessToken || allLoading) {
            // Use a random number for AB Testing
            if (!sessionStorage.getItem('ab_test_random_number')) {
                sessionStorage.setItem('ab_test_random_number', Math.floor(Math.random() * 10));
            }

            trackGTM('DataLayerInit', fetchDataLayer());
        }

        setUserEmail(user?.get('email'));
    }, [auth, loading, user]);

    React.useEffect(() => {
        // send myPlan, payment page viewed to iterable
        if (userEmail) {
            const { setEmail } = iterable.getIterableAuthObject();
            setEmail(userEmail)
                .then(() => {
                    Iterable.fetchInBrowserMessages()
                        .request()
                        .catch((error) => console.error('[FETCHING IN BROWER MESSAGES]:', error));
                })
                .catch((error) => console.error('[ITERABLE USER IDENTIFICATION]:', error));

            const currentUrl = window.location.pathname;
            let trackEvent = null;
            if (currentUrl.includes('/my-plan')) {
                trackEvent = 'MyPlanPageViewed';
            } else if (
                currentUrl.includes('/subscription/payment') ||
                currentUrl.includes('/subscription/renew')
            ) {
                trackEvent = 'ViewedPaymentPage';
            }
            if (trackEvent) {
                try {
                    Analytics.trackIterable('ViewedPaymentPage', {
                        email: userEmail,
                        pageName: currentUrl
                    });
                } catch (error) {
                    console.error('Error while tracking Iterable', error);
                }
            }
        }
    }, [userEmail]);

    return false;
}

const ConnectedPageViewTrack = connect((state) => ({
    auth: state.auth,
    user: state.user,
    paymentMethods: state.paymentMethods.data,
    loading: state.loading
}))(PageViewTrack);

// manage token and load data for user connected
const root = createRoot(document.createElement('div'));
root.render(
    <Provider store={store}>
        <AnalyticsProvider>
            <ConnectedPageViewTrack />
            <TrackingTwig />
        </AnalyticsProvider>
        <AuthProvider>
            <UserContext />
        </AuthProvider>
    </Provider>
);

// Initialize Signin Button
const signin = document.getElementById('signin');
if (signin) {
    insertElementBefore(
        <CookiesProvider>
            <Provider store={store}>
                <AnalyticsProvider>
                    <Signin />
                </AnalyticsProvider>
            </Provider>
        </CookiesProvider>,
        signin
    ).then(() => {
        // show modal when click on sign in button
        $('body').on('click', '.btn-signin', () => {
            document.dispatchEvent(new CustomEvent('open_signin_popup'));
        });
        if (window.location.hash === '#signin') {
            document.dispatchEvent(new CustomEvent('open_signin_popup'));
        }
    });
}

const btnContactCollection = document.getElementById('contact-collection');
if (btnContactCollection) {
    const btnsContact = document.querySelectorAll('.btn-contact-collection');
    btnsContact.forEach((btnContact) =>
        btnContact.addEventListener('click', () => {
            sweetAlert({
                content: (
                    <div className="collection-popup">
                        <div className="collection-popup-header">
                            <Icon
                                name="Cross"
                                onClick={() => {
                                    sweetAlert.close();
                                }}
                            />
                        </div>
                        <div className="collection-popup-content">
                            <h4 className="collection-popup-title">{i18n.t('collection:need-help')}</h4>
                            <p
                                className="collection-popup-text"
                                dangerouslySetInnerHTML={{
                                    __html: i18n.t('collection:contact-us-popup-text')
                                }}
                            />
                        </div>
                        <div className="collection-popup-buttons">
                            <button className="btn btn-primary btn-contact-collection-popup">
                                <Icon name="TelephoneHandset" />
                                <a
                                    href="https://homeexchangecollection.setmore.com/bookappointment"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {i18n.t('collection:book-a-call')}
                                </a>
                            </button>
                            <button className="btn btn-primary btn-contact-collection-popup">
                                <Icon name="Envelope" />
                                <a
                                    href={`mailto:joincollection@homeexchange.com?subject=${i18n.t(
                                        'collection:object-mail-contact-collection'
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {i18n.t('collection:send-mail')}
                                </a>
                            </button>
                        </div>
                    </div>
                ),
                closeOnClickOutside: true,
                buttons: false,
                className: 'collection-popup-modal'
            });
        })
    );
}

// init guest point tour for collection user
$('.gp-number').ready(() => {
    if (
        $('body').hasClass('he-collection') &&
        window.location.pathname !== '/collection/subscription/payment' &&
        window.location.pathname !== '/collection'
    ) {
        const { user } = store.getState();
        Tour.isViewed(termsOfUseTourName).then((isViewed) => {
            if (isViewed && user?.isFromApplicationFlowCollection()) {
                Tour.init(Tour.collectionGPTour.id);
            }
        });
    }
});

// Initialize Signup Button
const signup = document.getElementById('signup');
if (signup) {
    insertElementBefore(
        <CookiesProvider>
            <Provider store={store}>
                <CurrencyProvider>
                    <AnalyticsProvider>
                        <Signup />
                    </AnalyticsProvider>
                </CurrencyProvider>
            </Provider>
        </CookiesProvider>,
        signup
    ).then(() => {
        // show modal when click on sign in button
        $('body').on('click', '.btn-signup, [data-target="#mySignUpModal"], [href="#user-signup"]', (e) => {
            const version = $(e.currentTarget).data('context');
            document.dispatchEvent(
                new CustomEvent('open_signup_popup', {
                    detail: { version }
                })
            );
        });
        if (window.location.hash === '#signup' || window.location.hash === '#user-signup') {
            document.dispatchEvent(new CustomEvent('open_signup_popup'));
        }
    });
}

const OpenSignupPopup = () => {
    const location = useLocation();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        if (queryParams.get('showSignupPopup') === 'true') {
            document.dispatchEvent(new CustomEvent('open_signup_popup'));
        }
    }, [location.search]);

    return null;
};

createRoot(document.createElement('div')).render(
    <Provider store={store}>
        <Router>
            <OpenSignupPopup />
        </Router>
    </Provider>
);

// render search bars
const navSearchBars = document.querySelectorAll('.search-bar-wrapper .search-bar');
if (navSearchBars && navSearchBars.length > 0) {
    Array.prototype.forEach.call(navSearchBars, (searchBar) => {
        createRoot(searchBar).render(
            <Provider store={store}>
                <NavSearchBar />
            </Provider>
        );
    });
}

const iconHeaderHelp = document.getElementById('icon-Lifebuoy');
if (iconHeaderHelp) {
    createRoot(iconHeaderHelp).render(<Icon name="Lifebuoy" size="2xl" />);
}

const iconHeaderMessage = document.getElementById('icon-header-message');
if (iconHeaderMessage) {
    createRoot(iconHeaderMessage).render(
        <>
            <Icon name="Envelope" size="2xl" />
            {iconHeaderMessage.dataset.nbMessage > 0 && (
                <span className="badge badge-menu badge-absolute">{iconHeaderMessage.dataset.nbMessage}</span>
            )}
        </>
    );
}

// render search bar
const headerGuestpoint = document.getElementById('guestpoint_elt');
if (headerGuestpoint) {
    createRoot(headerGuestpoint).render(
        <Provider store={store}>
            <MenuGuestpoints />
        </Provider>
    );
}

// render LHS FAQ help menu
const lhsFAQHelp = document.getElementById('lhs-faq');
if (isconnected && lhsFAQHelp) {
    createRoot(lhsFAQHelp).render(
        <Provider store={store}>
            <LHSHelpMenuItem />
        </Provider>
    );
}

// render header profile
const hearderProfile = document.getElementById('profil_elt');
if (hearderProfile) {
    replaceElement(
        <Provider store={store}>
            <MenuProfile />
        </Provider>,
        hearderProfile
    ).then(() => {
        store.dispatch(fetchTopMenuNotifications());
    });
}

function ResponseRate({ user }) {
    const responseRateContainer = document.querySelectorAll('.header-user-response-rate');
    if (responseRateContainer) {
        responseRateContainer.forEach((element) => {
            createRoot(element).render(
                <Provider store={store}>
                    {user?.has('reactivity_level') && (
                        <ReactivityLevel reactivityLevel={user.get('reactivity_level')} />
                    )}
                </Provider>
            );
        });
    } else {
        responseRateContainer.forEach((element) => element.remove());
    }
}

const ConnectedResponseRate = connect((state) => ({
    user: state.user
}))(ResponseRate);

createRoot(document.createElement('div')).render(
    <Provider store={store}>
        <Router>
            <ConnectedResponseRate />
        </Router>
    </Provider>
);

// Initialize Stars
const stars = document.querySelectorAll('.react-stars');
[].forEach.call(stars, (element) => {
    const nbStars = parseFloat(element.getAttribute('data-stars'));
    createRoot(element).render(<Stars stars={nbStars} />);
});

// Initialize Upload Profile
const uploadProfile = document.querySelector('#upload-profile');
if (uploadProfile) {
    createRoot(uploadProfile).render(
        <Provider store={store}>
            <UploadProfile user={store.getState().user} homes={store.getState().homes} />
        </Provider>
    );
}

// Initialize Upload Home
const uploadHome = document.querySelector('#upload-home');
if (uploadHome) {
    const homeId = parseInt(uploadHome.getAttribute('data-homeId'), 10);
    const segmentCategory = uploadHome.getAttribute('data-segmentCategory');
    const explanations = Boolean(uploadHome.getAttribute('data-explanations'));
    const minImages = parseInt(uploadHome.getAttribute('data-minImages'), 10);
    createRoot(uploadHome).render(
        <Provider store={store}>
            <AnalyticsProvider>
                <UploadListWithCaption
                    homeId={homeId}
                    minImages={minImages}
                    explanations={explanations}
                    segmentCategory={segmentCategory}
                />
            </AnalyticsProvider>
        </Provider>
    );
}

const headbandEl = document.getElementById('headband');
const doNotDisplayOnThisPage =
    document.getElementById('billing-layout') || // payment page
    document.getElementById('sponsorship-page') || // sponsorship page
    document.getElementById('user-settings') || // user setting page
    document.getElementById('user-edit-2') || // profile edit page
    document.getElementById('home-edit-2') || // home edit page
    document.getElementById('messaging') || // messaging page
    document.getElementById('messaging-v2') || // messaging v2 page
    document.getElementById('collection-landing-page') || // landing page collection
    document.getElementById('block-sign-in-collection') || // block sign in collection page
    document.getElementById('verification-page'); // Account verification page

if (headbandEl && !doNotDisplayOnThisPage) {
    // Do not display headband on the messaging
    createRoot(headbandEl).render(
        <CookiesProvider>
            <Provider store={store}>
                <CurrencyProvider>
                    <Headband />
                </CurrencyProvider>
            </Provider>
        </CookiesProvider>
    );
}

const languageSwitcher = document.getElementById('language-switcher');
if (languageSwitcher) {
    createRoot(languageSwitcher).render(<LanguageSwitcher />);
}

const currencySwitcher = document.getElementById('currency-switcher');
if (currencySwitcher) {
    createRoot(currencySwitcher).render(
        <CookiesProvider>
            <Provider store={store}>
                <CurrencyProvider>
                    <CurrencySwitcher />
                </CurrencyProvider>
            </Provider>
        </CookiesProvider>
    );
}

const footer = document.getElementById('footer');

if (footer) {
    createRoot(footer).render(
        <Provider store={store}>
            <CurrencyProvider>
                <Footer />
            </CurrencyProvider>
        </Provider>
    );
}

const discoverCollection = document.getElementById('discover-collection');
if (discoverCollection) {
    createRoot(discoverCollection).render(
        <CookiesProvider>
            <Provider store={store}>
                <DiscoverCollection />
            </Provider>
        </CookiesProvider>
    );
}

const discoverCollectionMobile = document.getElementById('discover-collection-mobile');
if (discoverCollectionMobile) {
    createRoot(discoverCollectionMobile).render(
        <CookiesProvider>
            <Provider store={store}>
                <DiscoverCollection displayOnMobile />
            </Provider>
        </CookiesProvider>
    );
}

const termsOfUsePopup = document.getElementById('terms-of-use-popup');
if (termsOfUsePopup) {
    // ISL-1922 - Do not display TermsOfUse popup on user settings page if from email
    const fromEmail = Boolean(window.location.search.match(/source=email/i));
    const userSettingsPage = Boolean(window.location.href.match(/\/user\/settings/i));
    const userEditPage = Boolean(window.location.href.match(/\/user\/edit/i));
    if (!(fromEmail && (userSettingsPage || userEditPage))) {
        createRoot(termsOfUsePopup).render(
            <Provider store={store}>
                <TermsOfUsePopup />
            </Provider>
        );
    }
}

appendElementBefore(
    <Provider store={store}>
        <React.Fragment>
            <AnalyticsProvider>
                <EmailVerification />
            </AnalyticsProvider>
            <PhoneBanner />
        </React.Fragment>
    </Provider>,
    document.body
);

// Popup can be disabled by removing the environment variable
if (window && window.gtg && window.gtg.enableGPPopup) {
    const connectedLayout = document.getElementsByClassName('layout-login');
    // Only show on connected pages
    if (connectedLayout.length > 0) {
        const { user } = store.getState();
        if (user && user.get('tours') && !_.findWhere(user.get('tours'), { tour: 'gp_change_popup' })) {
            // Check that the user hasn't seen the popup about GP change already
            const content = (
                <div>
                    {i18n.t('common:gp_popup_description')}
                    <br />
                    <a href={i18n.t('url:blog_gp_popup')} target="_blank" rel="noopener noreferrer">
                        {i18n.t('common:seemore')}
                    </a>
                </div>
            );
            sweetAlert({
                title: i18n.t('common:gp_popup_title'),
                content,
                icon: 'info',
                className: 'gp-popup',
                closeOnEsc: false,
                closeOnClickOutside: false,
                buttons: i18n.t('common:gp_popup_button')
            }).then(() => {
                Api.UserTour.setTourAsViewed('gp_change_popup');
            });
        }
    }
}
